import { useEffect } from 'react';
import { GridBag, Gutter, Headline } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { CheckoutSummaryTranslations, CheckoutSummary as CheckoutSummaryReusable } from '@cdaas/reusable-components-daas';

import { ORDER_FLOW_CONSTANTS } from '../../../utils';
import { resetOrderState, selectCompletedOrder, selectDeliveryOption, selectSelectedUser } from '../../../store/order';
import { ApprovalStatus } from '@cdaas/api-clients/dist/order-openapi';
import { getExistingCustomerOrgFormPath, routes } from '../../App/routes';

// TODO
// In design there are possibility to show icon and details about approver if approver exists
// This is not implemented and need to be looked at. Reusable has the ability to show approver already.
export default function OrderCompleted() {
  const orderStore = useSelector(selectCompletedOrder);
  const deliveryOptionStore = useSelector(selectDeliveryOption);
  const selectedUserStore = useSelector(selectSelectedUser);

  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation('orderFlow');
  const { t: tGlobal } = useTranslation('global');

  useEffect(() => {
    const timeOutHandle = setTimeout(() => {
      if (!orderStore) {
        history.push(ORDER_FLOW_CONSTANTS.selectProduct);
      }
    }, 500);
    return () => clearTimeout(timeOutHandle);
    // want to run this only once at the beginning
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isApproved =
    orderStore?.customer_approval.status &&
    ([ApprovalStatus.Approved, ApprovalStatus.AutoApproved, ApprovalStatus.NotNeeded] as ApprovalStatus[]).includes(
      orderStore.customer_approval.status,
    );

  const handleNextClick = () => {
    const organizationId = orderStore?.ordered_to.external_organization_id;
    const userGroupId = selectedUserStore?.user_group?.id;
    const userId = selectedUserStore?.id;

    let url = '/';
    if (!selectedUserStore && organizationId) {
      url = `${getExistingCustomerOrgFormPath(organizationId)}/orders`;
    } else if (organizationId && userGroupId && userId) {
      url = `${getExistingCustomerOrgFormPath(organizationId)}${routes.userGroups}/${userGroupId}${routes.users}/${userId}`;
    }

    dispatch(resetOrderState());
    history.push(url);
  };

  // Currently there should be only one package with one end user when order is created for end user. In the future,
  // there may be multiple packages. In this case contact person of the organization is used.
  const givenName =
    orderStore?.packages.length === 1 ? orderStore?.packages[0].end_user.given_name : orderStore?.ordered_to.contact_person.given_name;
  const familyName =
    orderStore?.packages.length === 1 ? orderStore?.packages[0].end_user.family_name : orderStore?.ordered_to.contact_person.family_name;
  const email = orderStore?.packages.length === 1 ? orderStore?.packages[0].end_user.email : orderStore?.ordered_to.contact_person.email;

  const getOrderTitle = () => {
    if (!selectedUserStore) {
      return t('orderCompleted.orderTitleForOrganization');
    } else if (!givenName || !familyName) {
      return t('orderCompleted.orderTitleForInactiveUser', { email });
    } else {
      return t('orderCompleted.orderTitle', {
        given_name: givenName,
        family_name: familyName,
      });
    }
  };

  const translations: CheckoutSummaryTranslations = {
    orderTitle: getOrderTitle(),
    yourOrderText: t('orderCompleted.yourOrderText'),
    orderExplanationText: t(selectedUserStore ? 'orderCompleted.orderExplanationText' : 'orderCompleted.orderExplanationTextForOrganization'),
    detailsTitle: t('orderCompleted.detailsTitle'),
    approverIconTitle: t('orderCompleted.approverIconTitle'),
    employeeIconTitle: t('orderCompleted.employeeIconTitle'),
    deliveryIconTitle: t('orderCompleted.deliveryIconTitle'),
    ordererIconTitle: t('orderCompleted.ordererIconTitle'),
    deliveryEstimate1: t('orderCompleted.deliveryEstimate1'),
    deliveryEstimate2: t('orderCompleted.deliveryEstimate2'),
    nextButton: t(selectedUserStore ? 'orderCompleted.nextButton' : 'orderCompleted.nextButtonForOrganization'),
    backButton: '',
    deliveryEstimateNotAvailable: t('deliveryOptions.deliveryEstimateNotAvailable'),
    optionDelivered: t('deliveryOptions.optionDelivered'),
    countries: {
      Finland: tGlobal('countries.Finland'),
      Norway: tGlobal('countries.Norway'),
    },
  };

  return (
    <>
      <Gutter size={'layout-5'} />
      <Headline size={'lg'}>{t(`orderCompleted.headline${!isApproved ? 'Approval' : ''}`)}</Headline>
      <Gutter size={'layout-5'} />

      {!!orderStore && !!deliveryOptionStore && (
        <GridBag noGutter>
          <GridBag.Item sm={6}>
            <CheckoutSummaryReusable
              translations={translations}
              order={orderStore}
              deliveryMethod={deliveryOptionStore}
              showApprover={false}
              showEmployee={!!selectedUserStore}
              showDelivery={true}
              showOrderer={false}
              handleNextClick={handleNextClick}
            />
          </GridBag.Item>
          <GridBag.Item sm={6}></GridBag.Item>
        </GridBag>
      )}
    </>
  );
}
